import React, { FC, useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styled from 'styled-components';
import { Button, ButtonSize, ButtonColor, Table, TableRow } from '@code/innovance-react-ui-kit';

import {
  WarningText,
  Title,
  Subtitle,
  Separator,
  TableWrapper,
  Form,
} from 'src/front/components/FormStyles';
import CustomerSearchForm, {
  SearchFormData,
  customerSearchDefaultValues,
} from 'src/front/components/CustomerSearchForm';

import {
  useCustomerSearchDispatch,
  useCustomerSearchState,
  useClearDispatch,
} from 'src/front/contexts/OnlinePosContext';
import { Customer, PosActivation } from 'src/front/api/apiInstance';
import { PosCardsResponseData } from 'src/front/api/generated';

import { formatDate } from 'src/front/common/utils/dateTimeUtils';

const Outer = styled.div`
  padding: 0 0 20px;
`;

const customerTableHeaders = [
  'cardEntry_customerInfo_name_label',
  'cardEntry_customerInfo_middleName_label',
  'cardEntry_customerTable_birth_title',
  'cardEntry_customerInfo_gender_label',
  'cardEntry_customerTable_document_title',
];

const cardTableHeaders = [
  'posActivation_cardNumber_text',
  'posActivation_embossedName_text',
  'general_status_label',
  'general_subStatus_text',
  'general_currency_label',
  'general_expiredDate_label',
];

interface Props {
  onCardSelect?: (card: PosCardsResponseData, formValues: SearchFormData) => void;
  goToNextStep: (
    customerId: string,
    customerNumber: string,
    cardNumber: string,
    cardId: string,
    cardStatus: string
  ) => void;
}
export interface CustomerTableRow extends TableRow {
  customerNumber?: string;
}
const CardSearch: FC<Props> = ({ onCardSelect, goToNextStep }) => {
  const dispatch = useCustomerSearchDispatch();
  const clearCustomerSearch = useClearDispatch();

  const {
    customerSearchValues,
    customerPageNumber: initialCustomerPage,
    cardPageNumber: initialCardPage,
  } = useCustomerSearchState();
  const { t } = useTranslation();

  const [searchFormValues, setSearchFormValues] = useState<SearchFormData>(
    customerSearchValues || customerSearchDefaultValues
  );

  const [customerList, setCustomerList] = useState<CustomerTableRow[]>();
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerTableRow | undefined>(
    customerSearchValues?.selectedCustomer
  );

  const [customerPage, setCustomerPage] = useState<number>(initialCustomerPage || 1);
  const [hasCustomerNextPage, setHasCustomerNextPage] = useState<boolean>(false);
  const [customersLoading, setCustomersLoading] = useState<boolean>(false);

  const [cardList, setCardList] = useState<PosCardsResponseData[]>([]);
  const [selectedCard, setSelectedCard] = useState<TableRow | undefined>(
    customerSearchValues?.selectedCard
  );
  const [cardPage, setCardPage] = useState<number>(initialCardPage || 1);
  const [hasCardNextPage, setHasCardNextPage] = useState<boolean>(false);
  const [cardsLoading, setCardsLoading] = useState<boolean>(false);
  const [warning, setWarning] = useState<string>();
  const [customerWarning, setCustomerWarning] = useState<string>();

  const loadCustomerList = useCallback(
    async (
      pageNumber = customerPage,
      forceRefresh = false,
      formValues: SearchFormData = searchFormValues
    ): Promise<void> => {
      try {
        setCustomersLoading(true);
        const { birthDate, ...rest } = formValues;
        const { data } = await Customer.getCustomers({
          ...rest,
          birthDate: formatDate(birthDate),
          rowCountPerPage: 5,
          pageNumber: pageNumber || customerPage,
          refresh: !pageNumber || forceRefresh,
        });

        const customers = data.customerList.map((customer) => ({
          id: customer.customerId || '',
          columns: [
            `${customer.customerName} ${customer.customerSurname}`,
            customer.customerSecondName || '',
            `${customer.birthDate || ''} - ${customer.birthPlace || ''}`,
            customer.gender || '',
            `${customer.identityDocumentSerialCode} - ${customer.identityDocumentSerialNumber}`,
          ],
          customerNumber: customer.customerNumber,
        }));
        setCustomerList(customers);
        setHasCustomerNextPage(data.hasNextPage);
        setCustomerPage(pageNumber || 1);
        setCustomersLoading(false);
        setCustomerWarning(data.warning);
      } catch (error) {
        // TODO (ozgunbal): show error on modal or handle with error boundary
      }
    },
    [customerPage, searchFormValues]
  );

  const loadCardList = useCallback(
    async (pageNumber = cardPage, forceRefresh = false, customer = selectedCustomer) => {
      try {
        setCardsLoading(true);

        const { data } = await PosActivation.getPosCards({
          customerNumber: customer.customerNumber || '',
          rowCountPerPage: 5,
          pageNumber: pageNumber || cardPage,
          refresh: !pageNumber || forceRefresh,
        });
        setCardList(data.posCardList);
        setHasCardNextPage(data.hasNextPage);
        setCardPage(pageNumber || 1);
        setCardsLoading(false);
      } catch (err) {
        // todo(ctasci): it's handled with error boundary
      }
    },
    [cardPage, selectedCustomer]
  );

  const handleSelectCustomer = useCallback(
    async (customer: TableRow | undefined): Promise<void> => {
      clearCustomerSearch();

      setSelectedCustomer(customer);
      setSelectedCard(undefined);

      if (customer) await loadCardList(1, true, customer);
    },
    [clearCustomerSearch, loadCardList]
  );

  const handleSelectCard = (card: TableRow | undefined): void => {
    setSelectedCard(card);
    warning && setWarning(undefined);
  };

  useEffect(() => {
    const loadPreviousValues = async (): Promise<void> => {
      customerSearchValues?.selectedCustomer &&
        (await Promise.all([loadCustomerList(), loadCardList()]));
    };

    if (
      !customerList &&
      !cardList.length &&
      customerSearchValues?.selectedCustomer?.id &&
      customerSearchValues?.selectedCard?.id &&
      !cardsLoading &&
      !customersLoading
    ) {
      loadPreviousValues();
    }
  }, [
    cardList,
    cardsLoading,
    customerList,
    customerSearchValues,
    customersLoading,
    loadCardList,
    loadCustomerList,
  ]);

  const clearPreviousValues = (): void => {
    setCustomerList([]);
    setCardList([]);
    setSelectedCustomer(undefined);
    setSelectedCard(undefined);
    setCustomerWarning('');
    setWarning('');
  };

  const onSubmit = async (formData: SearchFormData): Promise<void> => {
    setSearchFormValues(formData);
    clearPreviousValues();
    await loadCustomerList(1, true, formData);
  };

  const handleContinue = (): void => {
    if (!selectedCard) {
      setWarning(t('cardEntry_productInfo_supplementary_chooseCard_label'));
    } else if (selectedCustomer && selectedCard) {
      const card = cardList.find((crd) => selectedCard.id === crd.cardId);
      if (!card) return;

      onCardSelect && onCardSelect(card, searchFormValues);

      // TODO: (emre.yildirim) this value keeping issue will be handled with mw
      dispatch({
        customerId: selectedCustomer.id,
        customerNumber: selectedCustomer.customerNumber,
        customerSearchValues: {
          ...searchFormValues,
          selectedCustomer,
          selectedCard,
        },
        customerPageNumber: customerPage,
        cardPageNumber: cardPage,
      });
      goToNextStep(
        selectedCustomer.id,
        selectedCustomer.customerNumber || '',
        card.cardNumber || '',
        card.cardId || '',
        card.cardStatus?.code || ''
      );
    }
  };

  const cardTableData: TableRow[] = cardList.map((card) => ({
    id: card.cardId || '',
    columns: [
      card.cardNumber || '',
      card.cardOwner || '',
      `${card.cardStatus?.value}`,
      `${card.cardSubStatus}`,
      card.currency || '',
      card.expireDate || '',
    ],
  }));

  return (
    <Form>
      <Outer>
        <Title>
          <Trans i18nKey="posActivation_title" />
        </Title>
        <Separator />
        <CustomerSearchForm formValues={searchFormValues} onSubmit={onSubmit} />
        {customerList ? (
          // TODO:(emre.yildirim) protect selected value
          <>
            <Separator />
            <Subtitle>
              <Trans i18nKey="posActivation_clientList_text" />
            </Subtitle>
            <TableWrapper>
              <Table
                emptyTableText={t('cardEntry_customerInfo_notFound_text')}
                hasNextPage={hasCustomerNextPage}
                headers={customerTableHeaders.map((head) => t(head))}
                items={customerList}
                loading={customersLoading}
                loadPageItems={loadCustomerList}
                onSelect={handleSelectCustomer}
                pageNumber={customerPage}
                selectedItem={selectedCustomer}
                widths={['19', '15', '30', '13', '23']}
                clearItemOnPageChange
                withRadio
              />
            </TableWrapper>
            {customerWarning ? <WarningText>{customerWarning}</WarningText> : null}

            {selectedCustomer ? (
              // TODO:(emre.yildirim) protect selected value
              <>
                <Separator />
                <Subtitle>
                  <Trans i18nKey="cardEntry_cardList_text" />
                </Subtitle>
                <TableWrapper>
                  <Table
                    emptyTableText={t('cardEntry_productInfo_supplementary_noCardFound_label')}
                    hasNextPage={hasCardNextPage}
                    headers={cardTableHeaders.map((head) => t(head))}
                    items={cardTableData}
                    loading={cardsLoading}
                    loadPageItems={loadCardList}
                    onSelect={handleSelectCard}
                    pageNumber={cardPage}
                    selectedItem={selectedCard}
                    clearItemOnPageChange
                    withRadio
                  />
                </TableWrapper>
                {warning ? <WarningText>{warning}</WarningText> : null}
                <Separator />
                <Button
                  color={ButtonColor.Primary}
                  onClick={handleContinue}
                  size={ButtonSize.Medium}
                  text={t('general_continue_button')}
                  type="button"
                />
              </>
            ) : null}
          </>
        ) : null}
      </Outer>
    </Form>
  );
};

export default CardSearch;
